import * as THREE from 'three';
import { RENDER_DISTANCE, FOG_NEAR_FACTOR, FOG_FAR_FACTOR } from '../constants';
import { GameSettings, DEFAULT_SETTINGS } from '../types';

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly ambientLight: THREE.AmbientLight;
  readonly directionalLight: THREE.DirectionalLight;
  private settings: GameSettings;

  constructor(canvas: HTMLCanvasElement) {
    this.settings = { ...DEFAULT_SETTINGS };

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setClearColor(0x87ceeb);
    this.renderer.shadowMap.enabled = false;

    this.scene = new THREE.Scene();

    const fogDist = RENDER_DISTANCE * 16;
    this.scene.fog = new THREE.Fog(0x87ceeb, fogDist * FOG_NEAR_FACTOR, fogDist * FOG_FAR_FACTOR);

    this.camera = new THREE.PerspectiveCamera(
      this.settings.fov,
      window.innerWidth / window.innerHeight,
      0.1,
      fogDist * 1.2,
    );

    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(this.ambientLight);

    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    this.directionalLight.position.set(100, 200, 100);
    this.scene.add(this.directionalLight);

    window.addEventListener('resize', this.onResize.bind(this));
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  updateSettings(settings: Partial<GameSettings>): void {
    Object.assign(this.settings, settings);
    this.camera.fov = this.settings.fov;
    this.camera.updateProjectionMatrix();
    const fogDist = this.settings.renderDistance * 16;
    this.scene.fog = new THREE.Fog(0x87ceeb, fogDist * FOG_NEAR_FACTOR, fogDist * FOG_FAR_FACTOR);
    this.camera.far = fogDist * 1.2;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
    this.renderer.dispose();
  }
}
